import { useAtom } from 'jotai';

import {
  type ClassModuleListItemViewModel,
  useGetApiV1ClassroomdisciplineClassModules,
} from '@/shared/api/generated';

import { classModuleFiltersAtom } from '../store/atoms/classModuleFiltersAtom';

/** Itens por página enviados à API (`PageSize`). */
export const CLASS_MODULE_PAGE_SIZE = 20;

/**
 * Envelope paginado que o backend retorna nas listagens
 * (`{ data, currentPage, totalPages, totalCount }`). O spec OpenAPI declara a
 * resposta como um array puro, mas em runtime o endpoint entrega este envelope
 * (mesmo padrão de outras listagens). O hook tolera ambos os formatos.
 */
interface ClassModulesEnvelope {
  readonly data?: ClassModuleListItemViewModel[] | null;
  readonly currentPage?: number;
  readonly totalPages?: number;
  readonly totalCount?: number;
}

/**
 * Hook para listar módulos de aula com filtros e paginação server-side.
 *
 * Lê o átomo de filtros, monta os query params do endpoint
 * `GET /api/v1/classroomdiscipline/class-modules` (com spreads condicionais) e
 * envolve o hook gerado pelo Kubb. Aceita tanto um array puro quanto o envelope
 * paginado do backend — quando o envelope traz `totalPages`/`totalCount`, essa
 * metadata é usada; caso contrário a próxima página é inferida por página cheia.
 *
 * @returns Dados da página atual e estado de loading/error/paginação.
 *
 * @example
 * ```tsx
 * const { data, isLoading, currentPage, hasNextPage, totalCount } = useClassModuleList();
 * ```
 */
export function useClassModuleList() {
  const [filters] = useAtom(classModuleFiltersAtom);

  const query = useGetApiV1ClassroomdisciplineClassModules({
    PageNumber: filters.page,
    PageSize: CLASS_MODULE_PAGE_SIZE,
    ...(filters.description ? { Description: filters.description } : {}),
    ...(filters.companyId !== null ? { CompanyId: filters.companyId } : {}),
    ...(filters.classStudentId !== null ? { ClassStudentId: filters.classStudentId } : {}),
    ...(filters.dateStart ? { DateStart: filters.dateStart } : {}),
    ...(filters.dateFinish ? { DateFinish: filters.dateFinish } : {}),
  });

  const raw = query.data as ClassModuleListItemViewModel[] | ClassModulesEnvelope | undefined;

  const data = Array.isArray(raw) ? raw : (raw?.data ?? []);
  const envelope = Array.isArray(raw) ? undefined : raw;

  const currentPage = envelope?.currentPage ?? filters.page;
  const totalPages = envelope?.totalPages;
  const totalCount = envelope?.totalCount;

  const hasNextPage =
    totalPages !== undefined ? currentPage < totalPages : data.length === CLASS_MODULE_PAGE_SIZE;

  return {
    data,
    isLoading: query.isLoading,
    isError: query.isError,
    currentPage,
    pageSize: CLASS_MODULE_PAGE_SIZE,
    hasNextPage,
    totalPages,
    totalCount,
  } as const;
}
